import React, { useMemo, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { httpsCallable } from 'firebase/functions';
import { IconX, IconCopy, IconCheck, IconGift } from '@tabler/icons-react';
import { functions } from '../firebase';
import useStyleGroups from '../hooks/useStyleGroups';
import { pickTemplateForDimension } from './StylePicker';
import { isZwiftMapStyleName } from '../utils/zwiftActivity';
import StyleSelectionSection from './create/StyleSelectionSection';
import DimensionSelectionSection from './create/DimensionSelectionSection';
import FormatSelectionSection from './create/FormatSelectionSection';
import LegendSection from './create/LegendSection';
import '../pages/CreatePage.css';
import './ReferralInviteBuilder.css';

const STYLES_PER_PAGE = 9;

// Inviter builds a map for a friend's Strava activity using the same picker components as
// CreatePage. Before sending, we resolve the friend from the activity (deriving their athlete id
// via the public embed endpoint, server-side) and tell the inviter if they're already a member.
// If so, the inviter can gift the map up front for 1 diamond (debited server-side), or cancel.
function ReferralInviteBuilder({ onClose }) {
  const { styleGroups, tagCategories, globalStyleOrder, hiddenStyles, isLoading } = useStyleGroups();

  const [activityInput, setActivityInput] = useState('');
  const [profileInput, setProfileInput] = useState('');
  const [resolveMissed, setResolveMissed] = useState(false);
  const [selectedStyleGroup, setSelectedStyleGroup] = useState(null);
  const [mapDimension, setMapDimension] = useState('3d');
  const [mapFormat, setMapFormat] = useState('portrait');
  const [customTitle, setCustomTitle] = useState('');
  const [distanceUnit, setDistanceUnit] = useState('metric');
  const [isLegendExpanded, setIsLegendExpanded] = useState(false);
  const [activeTag, setActiveTag] = useState(null);
  const [activeTagCategory, setActiveTagCategory] = useState(null);
  const [currentStylePage, setCurrentStylePage] = useState(0);

  const [phase, setPhase] = useState('form'); // 'form' | 'gift' | 'success'
  const [resolved, setResolved] = useState(null); // { athleteId, friendIsMember }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [inviteUrl, setInviteUrl] = useState('');
  const [copied, setCopied] = useState(false);

  // Lock background scroll while the modal is open (mobile).
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, []);

  // Drop hidden styles + the Zwift-only style (no activity context in the builder).
  const visibleStyleGroups = useMemo(() => (
    styleGroups
      .filter((g) => !hiddenStyles.includes(g.name))
      .filter((g) => !isZwiftMapStyleName(g.name))
  ), [styleGroups, hiddenStyles]);

  const availableTagCategories = useMemo(() => {
    if (visibleStyleGroups.length === 0 || Object.keys(tagCategories).length === 0) return {};
    return Object.entries(tagCategories).reduce((acc, [category, tags]) => {
      const availableTags = tags.filter((tag) =>
        visibleStyleGroups.some((g) => Array.isArray(g.tags) && g.tags.includes(tag))
      );
      if (availableTags.length > 0) acc[category] = availableTags;
      return acc;
    }, {});
  }, [tagCategories, visibleStyleGroups]);

  useEffect(() => {
    const names = Object.keys(availableTagCategories);
    if (names.length === 0) {
      if (activeTagCategory !== null) setActiveTagCategory(null);
      return;
    }
    if (!activeTagCategory || !availableTagCategories[activeTagCategory]) {
      setActiveTagCategory(names[0]);
      setActiveTag(null);
    }
  }, [availableTagCategories, activeTagCategory]);

  const sortedStyleGroups = useMemo(() => {
    let filtered = visibleStyleGroups;
    if (activeTag) {
      filtered = visibleStyleGroups.filter((g) => Array.isArray(g.tags) && g.tags.includes(activeTag));
    }
    const sorted = [...filtered];
    if (globalStyleOrder.length > 0) {
      sorted.sort((a, b) => {
        const ai = globalStyleOrder.indexOf(a.name);
        const bi = globalStyleOrder.indexOf(b.name);
        if (ai !== -1 && bi !== -1) return ai - bi;
        if (ai !== -1) return -1;
        if (bi !== -1) return 1;
        return a.name.localeCompare(b.name);
      });
    } else {
      sorted.sort((a, b) => a.name.localeCompare(b.name));
    }
    return sorted;
  }, [visibleStyleGroups, activeTag, globalStyleOrder]);

  useEffect(() => { setCurrentStylePage(0); }, [activeTag]);

  const totalStylePages = Math.ceil(sortedStyleGroups.length / STYLES_PER_PAGE);
  const paginatedStyles = useMemo(() => {
    const start = currentStylePage * STYLES_PER_PAGE;
    return sortedStyleGroups.slice(start, start + STYLES_PER_PAGE);
  }, [sortedStyleGroups, currentStylePage]);

  const handleStyleSelect = (group) => {
    setSelectedStyleGroup(group);
    if (mapDimension === '3d' && !group.has3D) setMapDimension('2d');
    if (mapDimension === '2d' && !group.has2D && group.has3D) setMapDimension('3d');
  };

  const selectedTemplate = selectedStyleGroup
    ? pickTemplateForDimension(selectedStyleGroup, mapDimension === '3d')
    : null;

  const canSubmit = Boolean(activityInput.trim() && selectedTemplate && !busy);

  // Create the invite doc (normal or gift) and show the shareable link.
  const finalizeInvite = async ({ athleteId, friendIsMember, isGift }) => {
    const createInvite = httpsCallable(functions, 'createReferralInvite');
    const result = await createInvite({
      friendActivityId: activityInput.trim(),
      athleteId: athleteId || undefined,
      friendIsMember: !!friendIsMember,
      isGift: !!isGift,
      promptTemplateId: selectedTemplate.id,
      mapDimension,
      mapOrientation: mapFormat,
      customTitle: customTitle.trim() || undefined,
      distanceUnit: distanceUnit === 'imperial' ? 'imperial' : undefined,
    });
    const inviteId = result.data?.inviteId;
    if (!inviteId) throw new Error('Something went wrong. Please try again.');
    setInviteUrl(`${window.location.origin}/invite/${inviteId}`);
    setPhase('success');
  };

  // Step 1: resolve the friend, then either create (non-member) or ask to gift (member).
  const handleSubmit = async () => {
    setError('');
    if (!activityInput.trim()) { setError("Enter your friend's Strava activity number or link."); return; }
    if (!selectedTemplate) { setError('Choose a style for the map.'); return; }
    setBusy(true);
    try {
      const resolve = httpsCallable(functions, 'resolveReferralFriend');
      const res = await resolve({
        activityInput: activityInput.trim(),
        profileInput: profileInput.trim() || undefined,
      });
      const data = res.data || {};

      if (data.needsProfile) {
        setResolveMissed(true);
        setError("Add your friend's Strava profile link above so we can check if they're already a member (most activities are followers-only, so we can't read it automatically) — or send it anyway as a new-member invite.");
        return;
      }

      setResolved({ athleteId: data.athleteId || null, friendIsMember: !!data.friendIsMember });
      if (data.friendIsMember) {
        setPhase('gift'); // member -> confirm the 1 diamond gift, or cancel
      } else {
        await finalizeInvite({ athleteId: data.athleteId, friendIsMember: false, isGift: false });
      }
    } catch (err) {
      console.error('resolveReferralFriend / createReferralInvite failed:', err);
      setError(err.message || 'Could not create the invite. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  // Inviter couldn't provide a profile: create a normal new-member invite. If the friend turns
  // out to be an existing member, the connect-time check will block the free path.
  const handleSendAnyway = async () => {
    setError('');
    setBusy(true);
    try {
      await finalizeInvite({ athleteId: null, friendIsMember: false, isGift: false });
    } catch (err) {
      console.error('createReferralInvite failed:', err);
      setError(err.message || 'Could not create the invite. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  // Member friend: gift the map. The backend debits the inviter 1 diamond atomically with
  // the invite creation, and rejects with failed-precondition if the balance is short.
  const handleConfirmGift = async () => {
    setError('');
    setBusy(true);
    try {
      await finalizeInvite({ athleteId: resolved?.athleteId, friendIsMember: true, isGift: true });
    } catch (err) {
      console.error('createReferralInvite (gift) failed:', err);
      setError(err.message || 'Could not create the gift.');
      setPhase('form');
    } finally {
      setBusy(false);
    }
  };

  const handleCancelGift = () => {
    // Nothing was created or charged — return to the form.
    setResolved(null);
    setPhase('form');
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) { /* clipboard unavailable */ }
  };

  let content;
  if (phase === 'success') {
    content = (
      <div className="referral-builder-success">
        <div className="referral-builder-success-emoji">🎉</div>
        <h2>Your invite is ready</h2>
        <p>
          Send this link to your friend. {resolved?.friendIsMember
            ? 'The gifted map will generate on their account when they open it.'
            : 'When they connect Strava as a new member, their map generates free and you both earn 5 💎.'}
        </p>
        <div className="referral-share-row">
          <input
            readOnly
            value={inviteUrl}
            className="referral-share-input"
            onFocus={(e) => e.target.select()}
          />
          <button className="referral-copy-btn" onClick={handleCopy}>
            {copied ? <IconCheck size={18} /> : <IconCopy size={18} />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
        <button className="referral-builder-secondary" onClick={onClose}>Done</button>
      </div>
    );
  } else if (phase === 'gift') {
    content = (
      <div className="referral-builder-gift">
        <div className="referral-builder-success-emoji"><IconGift size={44} /></div>
        <h2>Your friend is already a member</h2>
        <p>
          They won't get the free new-member map (and there's no referral reward), but you can
          gift them this map for <strong>1 💎</strong>.
        </p>
        {error && <div className="referral-builder-error">{error}</div>}
        <div className="referral-gift-actions">
          <button className="referral-builder-submit" disabled={busy} onClick={handleConfirmGift}>
            {busy ? 'Creating…' : 'Gift & create link (1 💎)'}
          </button>
          <button className="referral-builder-secondary" disabled={busy} onClick={handleCancelGift}>
            Cancel
          </button>
        </div>
      </div>
    );
  } else {
    content = (
      <div className="referral-builder-body">
        <h2 className="referral-builder-title">Make a map for a friend</h2>
        <p className="referral-builder-sub">
          Paste your friend's Strava activity, pick the look, and share the link.
        </p>

        <div className="referral-activity-field">
          <label className="legend-label">Friend's Strava activity number or link</label>
          <input
            type="text"
            className="legend-input"
            placeholder="e.g. 12345678901 or strava.com/activities/12345678901"
            value={activityInput}
            onChange={(e) => setActivityInput(e.target.value)}
          />
        </div>

        <div className="referral-activity-field">
          <label className="legend-label">Friend's Strava profile link</label>
          <input
            type="text"
            className="legend-input"
            placeholder="e.g. strava.com/athletes/12345678"
            value={profileInput}
            onChange={(e) => setProfileInput(e.target.value)}
          />
          <p className="referral-field-hint">
            Open the activity, click your friend's name to open their profile, then copy the URL. We
            use this to check if they're already a member (and can't read it from most activities).
          </p>
        </div>

        <StyleSelectionSection
          title="Choose style"
          isLoading={isLoading}
          isEmpty={sortedStyleGroups.length === 0}
          pageItems={paginatedStyles}
          selectedStyleName={selectedStyleGroup?.name || null}
          onSelectStyle={handleStyleSelect}
          tagFilter={{
            availableTagCategories,
            activeTagCategory,
            activeTag,
            onChangeTagCategory: setActiveTagCategory,
            onChangeTag: setActiveTag,
          }}
          page={currentStylePage}
          totalPages={totalStylePages}
          onPageChange={setCurrentStylePage}
        />

        {selectedStyleGroup && (
          <div className="dimension-selection-section">
            <h2 className="section-title">Choose dimension</h2>
            <DimensionSelectionSection
              styleGroup={selectedStyleGroup}
              dimension={mapDimension}
              onChangeDimension={setMapDimension}
            />
          </div>
        )}

        {selectedStyleGroup && (
          <FormatSelectionSection format={mapFormat} onChangeFormat={setMapFormat} />
        )}

        {selectedStyleGroup && (
          <LegendSection
            placeholder="Your friend's activity name"
            customTitle={customTitle}
            onChangeTitle={setCustomTitle}
            distanceUnit={distanceUnit}
            onChangeUnit={setDistanceUnit}
            isExpanded={isLegendExpanded}
            onToggleExpanded={setIsLegendExpanded}
            legendPreview={null}
          />
        )}

        {error && <div className="referral-builder-error">{error}</div>}

        <button className="referral-builder-submit" disabled={!canSubmit} onClick={handleSubmit}>
          {busy ? 'Checking…' : 'Create invite link'}
        </button>

        {resolveMissed && (
          <button className="referral-builder-textlink" disabled={busy} onClick={handleSendAnyway}>
            Send anyway as a new-member invite
          </button>
        )}
      </div>
    );
  }

  // Portal to <body> so the overlay isn't trapped inside a transformed/overflow-clipped
  // ancestor (e.g. the floating nav diamonds panel), which breaks position:fixed on mobile.
  return createPortal(
    <div className="referral-builder-overlay" onClick={onClose}>
      <div className="referral-builder-modal" onClick={(e) => e.stopPropagation()}>
        <button className="referral-builder-close" onClick={onClose} aria-label="Close">
          <IconX size={22} />
        </button>
        {content}
      </div>
    </div>,
    document.body,
  );
}

export default ReferralInviteBuilder;
